import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import Layout from '@/components/Layout';
import { PageHeader } from '@/components/ui/PageHeader';
import { toast } from 'sonner';
import { Briefcase, MapPin, Settings, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function PostJob() {
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    remote: false,
    job_type: 'full_time',
    salary_min: '',
    salary_max: '',
    requirements: ''
  });
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const navigate = useNavigate();

  // Saved locations state
  const [savedLocations, setSavedLocations] = useState([]);
  const [selectedLocId, setSelectedLocId] = useState('');

  useEffect(() => {
    const init = async () => {
      try {
        const userRes = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        setUser(userRes.data);

        // Fetch saved locations
        try {
          const locRes = await axios.get(`${BACKEND_URL}/api/employer/locations`, { withCredentials: true });
          const active = (locRes.data.locations || []).filter(l => l.is_active !== false);
          setSavedLocations(active);
          // Auto-select default job location if active
          const defId = locRes.data.default_job_location_id;
          if (defId && active.some(l => l.location_id === defId)) {
            setSelectedLocId(defId);
          }
        } catch { /* no locations — fine */ }
      } catch {
        navigate('/employer/register');
      }
      setInitLoading(false);
    };
    init();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLocId) {
      toast.error('Please select a location');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        remote: formData.remote,
        job_type: formData.job_type,
        salary_min: formData.salary_min ? parseInt(formData.salary_min) : null,
        salary_max: formData.salary_max ? parseInt(formData.salary_max) : null,
        requirements: formData.requirements || null,
        employer_location_id: selectedLocId,
        location: '',
      };
      await axios.post(`${BACKEND_URL}/api/jobs`, payload, { withCredentials: true });
      toast.success('Job posted successfully!');
      navigate('/employer/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post job');
    } finally {
      setLoading(false);
    }
  };

  const formatLocDisplay = (loc) => {
    const parts = [loc.city, loc.state].filter(Boolean).join(', ');
    return parts ? `${loc.label} — ${parts}` : loc.label;
  };

  if (initLoading) {
    return (
      <Layout user={user}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand" />
          </div>
        </div>
      </Layout>
    );
  }

  // Blocking state: no saved locations
  if (savedLocations.length === 0) {
    return (
      <Layout user={user}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <PageHeader icon={Briefcase} title="Post a Job" subtitle="Find qualified veteran candidates" />
          <Card className="p-10 border border-slate-200 shadow-sm text-center" data-testid="no-locations-block">
            <div className="flex flex-col items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center">
                <AlertCircle className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">Add a company location first</h3>
              <p className="text-sm text-slate-500 max-w-md">
                Before posting a job, you need at least one saved company location. Go to Employer Settings to add your office locations.
              </p>
              <Link to="/employer/settings">
                <Button className="bg-brand hover:bg-brand-hover text-white mt-2" data-testid="go-to-settings-btn">
                  <Settings className="w-4 h-4 mr-2" /> Go to Settings
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader icon={Briefcase} title="Post a Job" subtitle="Find qualified veteran candidates" />

        <Card className="p-8 border border-slate-200 shadow-sm" data-testid="post-job-form">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="title" className="text-slate-900 font-medium">Job Title *</Label>
              <Input data-testid="job-title-input" id="title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="h-12 mt-2" placeholder="Software Engineer, Project Manager, etc." required />
            </div>

            <div>
              <Label htmlFor="description" className="text-slate-900 font-medium">Job Description *</Label>
              <Textarea data-testid="job-description-textarea" id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="mt-2 min-h-[150px]" placeholder="Describe the role, responsibilities, and what you're looking for..." required />
            </div>

            {/* Location + Job Type — same row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-slate-900 font-medium">Location *</Label>
                <select data-testid="location-select"
                  value={selectedLocId}
                  onChange={(e) => setSelectedLocId(e.target.value)}
                  className="flex h-12 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm mt-2"
                  required>
                  <option value="">Select a location...</option>
                  {savedLocations.map(loc => (
                    <option key={loc.location_id} value={loc.location_id}>
                      {formatLocDisplay(loc)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="job_type" className="text-slate-900 font-medium">Job Type *</Label>
                <select data-testid="job-type-select" id="job_type"
                  value={formData.job_type}
                  onChange={(e) => setFormData({...formData, job_type: e.target.value})}
                  className="flex h-12 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm mt-2" required>
                  <option value="full_time">Full Time</option>
                  <option value="part_time">Part Time</option>
                  <option value="contract">Contract</option>
                  <option value="temporary">Temporary</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox data-testid="remote-checkbox" checked={formData.remote}
                onCheckedChange={(checked) => setFormData({...formData, remote: checked})} />
              <Label className="text-slate-700 text-sm">Remote position</Label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="salary_min" className="text-slate-900 font-medium">Minimum Salary</Label>
                <Input data-testid="salary-min-input" id="salary_min" type="number"
                  value={formData.salary_min}
                  onChange={(e) => setFormData({...formData, salary_min: e.target.value})}
                  className="h-12 mt-2" placeholder="60000" />
              </div>
              <div>
                <Label htmlFor="salary_max" className="text-slate-900 font-medium">Maximum Salary</Label>
                <Input data-testid="salary-max-input" id="salary_max" type="number"
                  value={formData.salary_max}
                  onChange={(e) => setFormData({...formData, salary_max: e.target.value})}
                  className="h-12 mt-2" placeholder="100000" />
              </div>
            </div>

            <div>
              <Label htmlFor="requirements" className="text-slate-900 font-medium">Requirements</Label>
              <Textarea data-testid="requirements-textarea" id="requirements"
                value={formData.requirements}
                onChange={(e) => setFormData({...formData, requirements: e.target.value})}
                className="mt-2 min-h-[120px]" placeholder="List required skills, experience, certifications, etc." />
            </div>

            <div className="flex gap-4 pt-4">
              <Button type="button" onClick={() => navigate('/employer/dashboard')}
                variant="outline" className="flex-1 h-12 border-slate-300 text-slate-700">
                Cancel
              </Button>
              <Button data-testid="post-job-submit-btn" type="submit" disabled={loading}
                className="flex-1 h-12 bg-brand hover:bg-brand-hover text-white">
                {loading ? 'Posting...' : 'Post Job'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </Layout>
  );
}

export default PostJob;
